
import { Component, OnInit } from '@angular/core';
import { DashboardService } from '../dashboard.service';

interface Category {
  id: string;
  title: string;
  description: string;
}

@Component({
  selector: 'app-category-selection',
  templateUrl: './category-selection.component.html',
  styleUrls: ['./category-selection.component.scss']
})
export class CategorySelectionComponent implements OnInit {
  selectedCategory: string | null = null;
  selectedSubCategory: string | null = null;
  selectedSubCategories: string[] = []; // For multiple EAM subcategory selection
  selectedCategories: string[] = []; // For sNDA multiple selection
  categories: Category[] = [];
  subCategories: string[] = [];
  categoryTitle: string = '';
  categorySubtitle: string = '';
  isMultipleSelection: boolean = false;

  private ndaCategories: string[] = [];
  private sndaCategories: string[] = [];
  private eamSubCategories: string[] = [];

  constructor(private dashboardService: DashboardService) {}

  ngOnInit() {
    console.log('CategorySelectionComponent initialized');
    
    // First load the PDUFA information
    this.dashboardService.pdufaInformation$.subscribe((res) => {
      this.ndaCategories = res?.categories?.NDA || [];
      this.sndaCategories = res?.categories?.sNDA || [];
      this.eamSubCategories = res?.sub_categories?.['Existing Active Moiety (EAM)'] || [];
      console.log('Loaded categories:', { nda: this.ndaCategories, snda: this.sndaCategories, eam: this.eamSubCategories });
    });

    // Then subscribe to filters
    this.dashboardService.filters$.subscribe(filters => {
      console.log('Filters received in category selection:', filters);
      
      // Update categories based on type first
      this.updateCategoriesBasedOnType(filters.type);
      
      // Then set the selected values
      this.selectedCategory = filters.category || null;
      this.selectedSubCategory = filters.subCategory || null;
      this.selectedSubCategories = filters.subCategories || [];
      this.selectedCategories = filters.categories || [];
      console.log("========54***",filters.categories)
      // Remove duplicates from selectedCategories array
      this.selectedCategories = [...new Set(this.selectedCategories)];
      
      console.log('Updated selectedCategory:', this.selectedCategory);
      console.log('Updated selectedSubCategory:', this.selectedSubCategory);
      console.log('Updated selectedSubCategories:', this.selectedSubCategories);
      console.log('Updated selectedCategories (after deduplication):', this.selectedCategories);
      console.log('selectedCategories length:', this.selectedCategories.length);
      console.log('isMultipleSelection:', this.isMultipleSelection);
      console.log('Available categories for selection:', this.categories);
      
      // If we have a selected category and it's EAM, show subcategories
      if (this.selectedCategory === 'Existing Active Moiety (EAM)' && this.eamSubCategories.length > 0) {
        this.subCategories = this.eamSubCategories;
      }
    });
  }

  selectCategory(category: string) {
    // Handle NDA single selection only
    console.log('Handling NDA single selection for:', category);
    this.selectedCategory = category;
    this.clearSubCategorySelections();
    
    if (category === 'Existing Active Moiety (EAM)') {
      // Show subcategory selector for EAM
      this.subCategories = this.eamSubCategories;
      this.dashboardService.updateFilters({ category: category });
    } else {
      // Update filters and navigate to next step for other NDA categories
      this.dashboardService.updateFilters({ category: category });
      this.dashboardService.selectCategory(category);
    }
  }

  toggleCategorySelection(category: string) {
    console.log('Toggling category selection for:', category);
    console.log('Current selectedCategories before toggle:', this.selectedCategories);
    console.log('isMultipleSelection:', this.isMultipleSelection);
    
    const index = this.selectedCategories.indexOf(category);
    if (index > -1) {
      this.selectedCategories.splice(index, 1);
      console.log('Removed category, new array:', this.selectedCategories);
    } else {
      this.selectedCategories.push(category);
      console.log('Added category, new array:', this.selectedCategories);
    }
    
    // Remove duplicates to prevent issues
    this.selectedCategories = [...new Set(this.selectedCategories)];
    
    console.log('Final selectedCategories length:', this.selectedCategories.length);
    console.log('Final selectedCategories array:', this.selectedCategories);
    this.dashboardService.updateFilters({ categories: this.selectedCategories });
  }

  selectSubCategory(subCategory: string) {
    this.selectedSubCategory = subCategory;
    this.dashboardService.updateFilters({ subCategory: subCategory });
    // Navigate to next step after selecting EAM subcategory
    if (this.selectedCategory) {
      this.dashboardService.selectCategory(this.selectedCategory);
    }
  }

  toggleSubCategorySelection(subCategory: string) {
    const index = this.selectedSubCategories.indexOf(subCategory);
    if (index > -1) {
      this.selectedSubCategories.splice(index, 1);
    } else {
      this.selectedSubCategories.push(subCategory);
    }
    this.dashboardService.updateFilters({ subCategories: this.selectedSubCategories });
  }

  proceedWithMultipleSubCategories() {
    if (this.selectedCategory) {
      this.dashboardService.updateFilters({ subCategories: this.selectedSubCategories });
      this.dashboardService.selectCategory(this.selectedCategory);
    }
  }

  proceedWithMultipleSelection() {
    console.log('Proceeding with multiple selection:', this.selectedCategories);
    if (this.selectedCategories.length > 0) {
      const categoriesString = this.selectedCategories.join(',');
      console.log('Categories string to send:', categoriesString);
      this.dashboardService.selectCategory(categoriesString);
    }
  }

  private updateCategoriesBasedOnType(type?: 'NDA' | 'sNDA') {
    console.log('Updating categories based on type:', type);
    if (type === 'NDA') {
      this.categoryTitle = 'NDA Categories';
      this.categorySubtitle = 'Select the NDA category for your application';
      this.categories = this.ndaCategories.map(cat => ({ id: cat, title: cat, description: '' }));
      this.isMultipleSelection = false;
      console.log('Set to NDA mode - isMultipleSelection:', this.isMultipleSelection);
      // Don't clear selections when updating categories - let filters handle the selection
    } else if (type === 'sNDA') {
      this.categoryTitle = 'sNDA Categories';
      this.categorySubtitle = 'Select one or more sNDA categories for your supplemental application';
      this.categories = this.sndaCategories.map(cat => ({ id: cat, title: cat, description: '' }));
      this.isMultipleSelection = true;
      console.log('Set to sNDA mode - isMultipleSelection:', this.isMultipleSelection);
      console.log('Available sNDA categories:', this.categories);
      // Don't clear selections when updating categories - let filters handle the selection
    }
  }

  private clearSubCategorySelections() {
    this.selectedSubCategory = null;
    this.selectedSubCategories = [];
    this.dashboardService.updateFilters({ 
      subCategory: null, 
      subCategories: [] 
    });
  }

  // Getter to check if EAM subcategory should be shown
  get showEamSubcategory(): boolean {
    return this.selectedCategory === 'Existing Active Moiety (EAM)' && this.subCategories.length > 0;
  }

  // Getter to check if multiple selection is enabled
  get canProceedWithMultiple(): boolean {
    return this.isMultipleSelection && this.selectedCategories.length > 0;
  }

  // Getter to check if subcategory is selected
  isSubCategorySelected(subCategory: string): boolean {
    return this.selectedSubCategories.includes(subCategory);
  }

  // Getter to check if can proceed with multiple subcategories
  get canProceedWithMultipleSubCategories(): boolean {
    return this.selectedSubCategories.length > 0;
  }

  // Getter to check if a category is selected
  isCategorySelected(categoryId: string): boolean {
    if (this.isMultipleSelection) {
      const isSelected = this.selectedCategories.includes(categoryId);
      console.log(`Checking if ${categoryId} is selected:`, isSelected, 'selectedCategories:', this.selectedCategories);
      return isSelected;
    } else {
      return this.selectedCategory === categoryId;
    }
  }
 
}
